import React from 'react';
import { useTranslation } from 'react-i18next';
import QuestionCard from './QuestionCard';
import TeamJersey from './TeamJersey';
import { AnswerButtonOverUnder } from './shared/AnswerButtonOverUnder';
// The threshold + stat name are already in the question copy ("…over 1.5
// Assists?"), so this header duplicated them (and showed the raw stat code,
// e.g. "soccer_assists"). Hidden per customer request — but still shown as a
// fallback when there's no question text (see render). Flip to `true` to force
// it always-on (DKPLAT-400). Figma CD over-under (10203:60669) shows this header.
const SHOW_STAT_HEADER = false;

export interface OverUnderPickProps {
  questionId: string;
  questionNumber?: number;
  points: number;
  earnedPoints?: number;

  // Match-level markets (e.g. DKPLAT-500 Total) have no player/team subject.
  // When true, the left panel shows the matchup + logos instead of a jersey
  // and player name (which would otherwise fall back to "U. Player").
  isMatchLevel?: boolean;

  // Player data (from question_metadata.grading.config)
  playerName: string;
  playerPosition: string;
  playerJerseyUrl: string; // from main_image
  playerTeamLogoUrl?: string; // Team logo to show in top right of player image

  // Stat data (from question_metadata + question_subtext)
  statValue: number;
  statType: string;

  // Match data (from sportMatchData)
  homeTeamName?: string;
  awayTeamName?: string;
  homeTeamLogoUrl?: string; // used by the match-level layout
  awayTeamLogoUrl?: string; // used by the match-level layout
  matchTime: string;

  // Popularity percentages
  overPercentage?: number;
  underPercentage?: number;

  // Selection
  selectedAnswer?: 'over' | 'under';
  onAnswerSelect: (answer: 'over' | 'under' | undefined) => void;

  // Question copy (matches other pick components — without this the card
  // shows only the threshold and stat label, which reads as raw data
  // rather than a question).
  questionText?: string;
  subtext?: string;

  // State
  isCorrect?: boolean | null;
  disabled?: boolean;
  locked?: boolean;
  questionMetadata?: { dailyDouble?: boolean };
}

const OverUnderPick: React.FC<OverUnderPickProps> = ({
  questionId,
  questionNumber,
  points,
  earnedPoints,
  isMatchLevel = false,
  playerName,
  playerPosition,
  playerJerseyUrl,
  playerTeamLogoUrl,
  statValue,
  statType,
  homeTeamName,
  awayTeamName,
  homeTeamLogoUrl,
  awayTeamLogoUrl,
  matchTime,
  overPercentage,
  underPercentage,
  selectedAnswer,
  onAnswerSelect,
  questionText,
  subtext,
  isCorrect,
  disabled = false,
  locked = false,
  questionMetadata: _questionMetadata,
}) => {
  // Daily Double chrome is unused for Circus Daily — ignore metadata styling.
  const isDailyDouble = false;
  const { t } = useTranslation();

  const handleAnswerClick = (answer: 'over' | 'under') => {
    if (disabled || locked) {
      return;
    }

    // Toggle logic: click same answer to deselect
    if (selectedAnswer === answer) {
      onAnswerSelect(undefined);
    } else {
      onAnswerSelect(answer);
    }
  };

  return (
    <QuestionCard
      points={points}
      earnedPoints={earnedPoints}
      questionNumber={questionNumber}
      isDailyDouble={isDailyDouble}
      isCorrect={isCorrect}
      data-testid={`question-${questionId}`}
      data-test-type="over_under"
    >
      {/* Subtext (optional) — matches other pick components' top-of-card line */}
      {subtext && (
        <span
          data-testid="over-under-subtext"
          className="text-muted-foreground font-sans font-normal block text-center text-sm"
        >
          {subtext}
        </span>
      )}

      {questionText && (
        <span
          data-testid="over-under-question"
          className="text-white font-sans font-normal block text-center text-body-default"
        >
          {questionText}
        </span>
      )}

      {/* Stat Header (threshold + stat name). Normally hidden — it duplicates the
          question copy ("…over 1.5 Assists?"). Shown as a fallback when there's no
          question text, so a title-less card (admin preview / data missing a title)
          still has a prompt. Force it always-on via SHOW_STAT_HEADER. */}
      {(SHOW_STAT_HEADER || !questionText) && (
        <div className="flex flex-col gap-0.5 items-center text-center text-white w-full">
          <div className="font-sans text-header-01 w-full">{statValue}</div>
          <div className="font-sans text-body-default w-full">{statType}</div>
        </div>
      )}

      {/* Player card + Over/Under — Figma container bg #121212, rounded-xl */}
      <div className="bg-[#121212] rounded-xl overflow-hidden w-full">
        <div className="flex items-end w-full min-h-[168px]">
          {/* Left Column: match panel for match-level totals (no player), or
              jersey + player info for player/team over-unders. */}
          {isMatchLevel ? (
            <div className="relative flex flex-1 flex-col items-center justify-center min-w-0 py-3 px-2 text-center">
              {(homeTeamLogoUrl || awayTeamLogoUrl) && (
                <div className="flex items-center gap-2 mb-1">
                  {homeTeamLogoUrl && (
                    <img src={homeTeamLogoUrl} alt={homeTeamName || 'Home'} className="w-8 h-8" />
                  )}
                  <span className="text-muted-foreground text-xs">@</span>
                  {awayTeamLogoUrl && (
                    <img src={awayTeamLogoUrl} alt={awayTeamName || 'Away'} className="w-8 h-8" />
                  )}
                </div>
              )}
              {(homeTeamName || awayTeamName) && (
                <div className="text-white text-sm font-sans font-semibold capitalize whitespace-nowrap">
                  {homeTeamName} @ {awayTeamName}
                </div>
              )}
              {matchTime && <div className="text-muted-foreground text-xs mt-1 font-sans">{matchTime}</div>}
            </div>
          ) : (
            <div className="relative flex flex-1 flex-col items-stretch min-w-0 min-h-[168px]">
              <div className="relative flex-1 min-h-[72px] w-full">
                {playerTeamLogoUrl && (
                  <img
                    src={playerTeamLogoUrl}
                    alt="Team"
                    className="absolute left-1.5 top-1.5 size-10 object-contain z-10"
                  />
                )}
                <div className="absolute inset-x-0 bottom-0 flex justify-center">
                  <TeamJersey size="md" jerseyUrl={playerJerseyUrl} />
                </div>
              </div>
              <div className="border-t-2 border-surface flex flex-col gap-1 items-center text-center capitalize px-2 py-3">
                <p className="text-white font-sans text-label-bold truncate w-full whitespace-nowrap">
                  {playerName}
                </p>
                {playerPosition && (
                  <p className="text-muted-foreground font-sans text-label w-full">{playerPosition}</p>
                )}
                {(homeTeamName || awayTeamName) && (
                  <p className="text-muted-foreground text-xs font-sans">
                    {homeTeamName} @ {awayTeamName}
                  </p>
                )}
                {matchTime && <p className="text-muted-foreground text-xs font-sans">{matchTime}</p>}
              </div>
            </div>
          )}

          {/* Right Column: Over/Under Buttons */}
          <div className="flex flex-1 flex-col gap-2 min-w-0 min-h-[168px] border-l-2 border-surface p-2 justify-end">
            <AnswerButtonOverUnder
              answer="over"
              label={t('question.over')}
              isSelected={selectedAnswer === 'over'}
              percentage={overPercentage}
              onClick={() => handleAnswerClick('over')}
              disabled={disabled}
              locked={locked}
              isCorrect={selectedAnswer === 'over' ? isCorrect : undefined}
              testId={`${questionId}-over`}
              isDailyDouble={isDailyDouble}
              className="flex-1 min-h-0"
            />
            <AnswerButtonOverUnder
              answer="under"
              label={t('question.under')}
              isSelected={selectedAnswer === 'under'}
              percentage={underPercentage}
              onClick={() => handleAnswerClick('under')}
              disabled={disabled}
              locked={locked}
              isCorrect={selectedAnswer === 'under' ? isCorrect : undefined}
              testId={`${questionId}-under`}
              isDailyDouble={isDailyDouble}
              className="flex-1 min-h-0"
            />
          </div>
        </div>
      </div>
    </QuestionCard>
  );
};

export default OverUnderPick;
